import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import {
  DownloadIcon,
  LayersIcon,
  TableIcon,
  UsersIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { useActiveStudentsByClassQuery } from "../api/queries";
import {
  ActiveStudentsByClassReportFilter,
  type ActiveStudentsByClassReportSearch,
} from "../ui/ActiveStudentsByClassReportFilter";
import { ActiveStudentsByClassReportTable } from "../ui/ActiveStudentsByClassReportTable";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function useReportQuery(search: ActiveStudentsByClassReportSearch) {
  return useMemo(
    () => ({
      classTypeId: search.classTypeId,
      levelId: search.levelId,
      groupId: search.groupId,
      sortBy: search.sortBy ?? "count",
      includeEmpty: search.includeEmpty !== "false",
    }),
    [
      search.classTypeId,
      search.levelId,
      search.groupId,
      search.sortBy,
      search.includeEmpty,
    ],
  );
}

export function ActiveStudentsByClassReportPage() {
  const search = useSearch({
    from: "/app/reports/active-students-by-class",
  }) as ActiveStudentsByClassReportSearch;

  const query = useReportQuery(search);
  const { data, isLoading } = useActiveStudentsByClassQuery(query);

  const items = data?.items ?? [];
  const asOfDate = data?.asOfDate;
  const totalDistinct = data?.totalDistinctStudents ?? 0;
  const classesWithStudents = items.filter((r) => r.activeStudentCount > 0)
    .length;

  const exportCsv = useCallback(() => {
    const header = "ປະເພດຄລາດສ໌,ລະດັບ,ນັກຮຽນ Active";
    const rows = items.map(
      (r) =>
        `${escapeCsvCell(r.classTypeName)},${escapeCsvCell(r.levelName)},${escapeCsvCell(r.activeStudentCount)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `active-students-by-class-${asOfDate ?? "report"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [items, asOfDate]);

  const barChartData = useMemo(
    () =>
      items
        .filter((r) => r.activeStudentCount > 0)
        .slice(0, 12)
        .map((r) => ({
          label:
            `${r.classTypeName} · ${r.levelName}`.length > 24
              ? `${r.classTypeName.slice(0, 10)}… ${r.levelName}`
              : `${r.classTypeName} · ${r.levelName}`,
          count: r.activeStudentCount,
        })),
    [items],
  );

  const barChartConfig: ChartConfig = {
    count: { label: "ນັກຮຽນ", color: "var(--chart-1)" },
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ນັກຮຽນ Active ຕາມຄລາສ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ນັກຮຽນ Active ຕາມຄລາສ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ນັບນັກຮຽນທີ່ມີແພັກເກດຄລາດສ໌ active ແລະ ເຊັກຊື່ໄດ້ຕາມປະເພດຄລາດສ໌ + ລະດັບ
              {asOfDate ? (
                <>
                  {" "}
                  · ຂໍ້ມູນ ณ {formatDate(asOfDate)}
                </>
              ) : null}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={isLoading || items.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫຼດ CSV
          </Button>
        </div>

        <ActiveStudentsByClassReportFilter />

        <ReportSummaryStatGrid columns={3}>
          <ReportSummaryStatCard
            title="ນັກຮຽນ Active (ບໍ່ຊ້ຳ)"
            icon={UsersIcon}
            tone="students"
            isLoading={isLoading}
            value={reportSummaryCount(totalDistinct)}
          />
          <ReportSummaryStatCard
            title="ຄລາສທີ່ມີນັກຮຽນ"
            icon={LayersIcon}
            tone="attendance"
            isLoading={isLoading}
            value={reportSummaryCount(classesWithStudents)}
          />
          <ReportSummaryStatCard
            title="ລວມຕາມຕາຕະລາງ"
            icon={TableIcon}
            tone="packages"
            isLoading={isLoading}
            value={reportSummaryCount(
              items.reduce((s, r) => s + r.activeStudentCount, 0),
            )}
          />
        </ReportSummaryStatGrid>

        {barChartData.length > 0 ? (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">
                Top ຄລາສຕາມຈຳນວນນັກຮຽນ Active
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                data={barChartData}
                config={barChartConfig}
                xDataKey="label"
                valueDataKey="count"
                xAxisTickFormatter={(v) =>
                  typeof v === "string" && v.length > 15
                    ? `${v.slice(0, 12)}...`
                    : String(v)
                }
                className="mx-auto aspect-video max-h-[320px]"
              />
            </CardContent>
          </Card>
        ) : null}

        <ActiveStudentsByClassReportTable isLoading={isLoading} data={items} />
      </Main>
    </>
  );
}
